import React, { useMemo } from 'react';
import RangeSelectorRow from './data/RangeSelectorRow';
import CollapsibleSection from './data/CollapsibleSection';
import WaterQualityChart from './data/WaterQualityChart';
import MetChart from './data/MetChart';
import WaveChart from './data/WaveChart';
import DirectionRoses from './data/DirectionRoses';
import OxygenSaturationChart from '../OxygenSaturationChart';
import { toEasternPlotDate } from '../../utils/sensorConfig';

const BLOOM_SENSOR_ORDER = ['pc', 'pc_fluorometer', 'chla', 'chla_fluorometer', 'turbidity_fluorometer'];
const LAKE_STRUCTURE_SENSOR_ORDER = ['temperature', 'temperature_rdo_corrected', 'o2_25', 'o2_rdo', 'o2_rdo_saturation', 'conductivity'];

const BLOOM_HELPER_TEXT = {
  pc: "A pigment unique to cyanobacteria — the earliest and clearest signal of a developing bloom.",
  pc_fluorometer: "A pigment unique to cyanobacteria — the earliest and clearest signal of a developing bloom.",
  chla: 'A measure of total algae in the water, most of it harmless.',
  chla_fluorometer: 'A measure of total algae in the water, most of it harmless.',
  turbidity_fluorometer: 'Water cloudiness. Spikes can accompany runoff, algae blooms, or sediment resuspension.',
};

function ChartSlot({ loading, error, children }) {
  if (error) {
    return (
      <div style={{
        background: '#fff', border: '1px solid #f0cfc9', borderRadius: 14,
        padding: '40px 24px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 10, marginBottom: 16,
      }}>
        <span style={{ fontSize: 22 }}>⚠</span>
        <span style={{ color: '#9e2b25', fontSize: 15, fontWeight: 600 }}>Couldn't load sensor data</span>
        <span style={{ color: '#6f6a5f', fontSize: 14, textAlign: 'center', maxWidth: 380 }}>{error}</span>
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{
        background: '#fff', border: '1px solid #ece7db', borderRadius: 14,
        padding: '48px 24px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 14, marginBottom: 16,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          border: '3px solid #e2e8f0', borderTopColor: '#46708a',
          animation: 'spin 0.8s linear infinite',
        }} />
        <span style={{ color: '#6f6a5f', fontSize: 15 }}>Loading sensor data…</span>
      </div>
    );
  }
  return children;
}

export default function WaterQualityDataTab({
  transformedData, gridId, rangeKey, setRangeKey, rangeLabel, launchDate,
  isAdmin, loadingPriority1, loadingPriority2, loadingPriority3, dataError,
  startDateISO, endDateISO,
}) {
  // Shared across every chart so the x-axis always spans the full selected range —
  // even when a given sensor only has data for part of it.
  const xRange = useMemo(
    () => (startDateISO && endDateISO
      ? [toEasternPlotDate(startDateISO), toEasternPlotDate(endDateISO)]
      : undefined),
    [startDateISO, endDateISO]
  );

  return (
    <main style={{ padding: '0 0 48px' }}>
      <RangeSelectorRow
        rangeKey={rangeKey}
        setRangeKey={setRangeKey}
        rangeLabel={rangeLabel}
        launchDate={launchDate}
      />
      <p style={{ margin: '-12px 0 20px', fontSize: 12.5, color: '#94a3b8' }}>
        All times shown in US/Eastern (ET).
      </p>

      <CollapsibleSection
        title="Bloom indicators"
        subtitle="Phycocyanin is the buoy's measure of cyanobacteria; the other readings add context. Some cyanobacteria are always present in a healthy lake, so a low, steady reading is normal — it's sharp spikes, sustained high levels, and surface scum that signal a developing bloom."
        defaultOpen
      >
        <ChartSlot loading={loadingPriority1} error={dataError}>
          <WaterQualityChart
            transformedData={transformedData}
            gridId={gridId}
            isAdmin={isAdmin}
            sensorOrder={BLOOM_SENSOR_ORDER}
            helperTextMap={BLOOM_HELPER_TEXT}
            xRange={xRange}
          />
        </ChartSlot>
      </CollapsibleSection>

      <CollapsibleSection
        title="Lake Structure — Temperature & Oxygen"
        subtitle="Temperature and dissolved oxygen at the surface and near the bottom. When the surface and bottom temperature lines spread apart, the lake is stratified — warm water on top, cooler water below. When they come together, the lake is mixing, which can stir nutrients and cyanobacteria up from the bottom. Reading oxygen and temperature alongside the bloom indicators above can help explain why a bloom is developing."
        defaultOpen
      >
        <ChartSlot loading={loadingPriority1} error={dataError}>
          <WaterQualityChart
            transformedData={transformedData}
            isAdmin={isAdmin}
            sensorOrder={LAKE_STRUCTURE_SENSOR_ORDER}
            xRange={xRange}
          />
        </ChartSlot>
      </CollapsibleSection>

      <CollapsibleSection
        title="Bottom Oxygen"
        subtitle="Dissolved oxygen as a percentage of what the water can hold at its current temperature. When it runs low (hypoxia), sediment can release phosphorus that fuels cyanobacteria blooms — an early warning, not just a fish-health metric."
        defaultOpen
      >
        <ChartSlot loading={loadingPriority1} error={dataError}>
          <OxygenSaturationChart transformedData={transformedData} xRange={xRange} />
        </ChartSlot>
      </CollapsibleSection>

      {isAdmin && (
        <CollapsibleSection
          title="Weather & waves"
          subtitle="Wind, air, solar, and precipitation conditions, plus wave height and direction from the buoy. Calm surfaces let buoyant cyanobacteria rise and form scum, and steady onshore wind can push it toward one shoreline."
          defaultOpen
        >
          <ChartSlot loading={loadingPriority1} error={dataError}>
            <MetChart
              transformedData={transformedData}
              rangeKey={rangeKey}
              rangeLabel={rangeLabel}
              loadingPriority2={loadingPriority2}
              xRange={xRange}
            />
          </ChartSlot>
          <ChartSlot loading={loadingPriority1 || loadingPriority3} error={dataError}>
            <WaveChart
              transformedData={transformedData}
              rangeKey={rangeKey}
              rangeLabel={rangeLabel}
              loadingPriority3={loadingPriority3}
              xRange={xRange}
            />
          </ChartSlot>
          <ChartSlot loading={loadingPriority1 || loadingPriority3} error={dataError}>
            <DirectionRoses transformedData={transformedData} />
          </ChartSlot>
        </CollapsibleSection>
      )}
    </main>
  );
}
